import { useEffect, useState } from 'preact/hooks';
import { PHASE_DURATIONS, type PhaseName } from '@gazette/shared';
import { muted, toggleMuted } from '../../audio/sfx.ts';
import { locale, setLocale, t } from '../../i18n/index.ts';
import { actions, connection, lastError, view } from '../../net/client.ts';
import { Icon } from './Icon.tsx';
import { Timer } from './Timer.tsx';

export function SoundToggle() {
  const off = muted.value;
  return (
    <button type="button" class="icon-btn" onClick={toggleMuted} aria-pressed={!off} aria-label={off ? t('sound.unmute') : t('sound.mute')}>
      <Icon name={off ? 'mute' : 'sound'} />
    </button>
  );
}

export function LangToggle() {
  return (
    <button type="button" class="icon-btn icon-btn--text" onClick={() => setLocale(locale.value === 'fr' ? 'en' : 'fr')} aria-label={t('lang.label')}>
      <Icon name="lang" />
      <span lang={locale.value === 'fr' ? 'en' : 'fr'}>{locale.value === 'fr' ? 'EN' : 'FR'}</span>
    </button>
  );
}

/** Two taps to leave: the first arms the button, so a stray thumb doesn't drop you out. */
export function LeaveButton({
  label = t('menu.leave'),
  confirm = t('menu.leave.confirm'),
  class: className = 'icon-btn',
  showLabel = false,
}: {
  label?: string;
  confirm?: string;
  class?: string;
  showLabel?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const id = window.setTimeout(() => setArmed(false), 3500);
    return () => window.clearTimeout(id);
  }, [armed]);
  return (
    <button
      type="button"
      class={`${className} ${armed ? 'is-armed' : ''}`}
      onClick={() => (armed ? void actions.leave() : setArmed(true))}
      aria-label={showLabel ? undefined : armed ? confirm : label}
    >
      <Icon name="leave" />
      {(armed || showLabel) && <span>{armed ? confirm : label}</span>}
    </button>
  );
}

const TIMED: PhaseName[] = ['confessions', 'writing', 'publication', 'investigation', 'reveal', 'plume'];

export function GameHeader() {
  const current = view.value!;
  const phase = current.phase;
  const endsAt = 'endsAt' in phase ? phase.endsAt : 0;
  const total = TIMED.includes(phase.name) ? PHASE_DURATIONS[current.settings.pace][phase.name as keyof (typeof PHASE_DURATIONS)['normal']] : undefined;
  return (
    <header class="game-header" data-occlude>
      <p class="game-header__case">
        {t('lobby.case')} <strong>{current.code}</strong>
      </p>
      <p class="game-header__phase">{phase.name === 'lobby' ? t('lobby.suspects') : t(`phase.${phase.name}`)}</p>
      <div class="game-header__tools">
        {endsAt > 0 && <Timer endsAt={endsAt} total={total} />}
        {phase.name === 'lobby' && <LangToggle />}
        <SoundToggle />
        <LeaveButton />
      </div>
    </header>
  );
}

export function ConnectionBanner() {
  if (connection.value !== 'offline') return null;
  return (
    <p class="connection-banner" role="status">
      {t('connection.lost')}
    </p>
  );
}

/** The latest refusal from the server, in the player's words. */
export function ErrorNotice() {
  const error = lastError.value;
  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => (lastError.value = null), 6000);
    return () => window.clearTimeout(id);
  }, [error]);
  if (!error) return null;
  return (
    <p class="error-notice" role="alert">
      {t(`error.${error}`)}
      <button type="button" class="icon-btn" onClick={() => (lastError.value = null)} aria-label={t('pub.close')}>
        <Icon name="close" size={18} />
      </button>
    </p>
  );
}
